/**
 * Script pour marquer les clients existants comme "clients" dans le système unifié
 * 
 * Ce script ajoute les champs nécessaires aux clients existants pour qu'ils apparaissent
 * correctement dans le pipeline unifié prospects/clients
 */

import { db } from '../firebase';
import { 
  collection, 
  getDocs, 
  updateDoc, 
  doc,
  Timestamp 
} from 'firebase/firestore';
import { ENTITY_TYPES, CLIENT_STATUS, COMMERCIAL_STATUS } from '../services/unifiedClientService';

/**
 * Marque tous les clients existants comme "clients" dans le système unifié
 */
export const marquerClientsExistants = async () => {
  try {
    console.log('🔄 Début marquage clients existants...');
    
    // Récupérer tous les clients existants
    const clientsCollection = collection(db, 'clients');
    const clientsSnapshot = await getDocs(clientsCollection);
    
    let clientsMarques = 0;
    let erreurs = 0;

    for (const clientDoc of clientsSnapshot.docs) {
      try {
        const clientData = clientDoc.data();
        
        // Vérifier si le client a déjà été marqué
        if (clientData.typeEntite && clientData.statutClient) {
          console.log(`✅ Client ${clientDoc.id} déjà marqué comme ${clientData.typeEntite}`);
          continue;
        }

        // Préparer les données de mise à jour
        const updateData = {
          typeEntite: ENTITY_TYPES.CLIENT,
          statutClient: CLIENT_STATUS.ACTIF,
          statutCommercial: COMMERCIAL_STATUS.SIGNE, // Les clients existants sont considérés comme signés
          dateModification: Timestamp.now(),
          // Conserver les données existantes
          ...clientData
        };

        // Ajouter la date de création si elle n'existe pas
        if (!clientData.dateCreation) {
          updateData.dateCreation = Timestamp.now();
        }

        // Ajouter l'historique si nécessaire
        if (!clientData.historique) {
          updateData.historique = [
            {
              date: Timestamp.now(),
              evenement: 'marquage_systeme_unifie',
              details: 'Client existant marqué comme client dans le système unifié',
              utilisateur: 'system'
            }
          ];
        } else {
          // Ajouter l'événement à l'historique existant
          updateData.historique = [
            ...clientData.historique,
            {
              date: Timestamp.now(),
              evenement: 'marquage_systeme_unifie',
              details: 'Client existant marqué comme client dans le système unifié',
              utilisateur: 'system'
            }
          ];
        }

        // Mettre à jour le document
        const clientRef = doc(db, 'clients', clientDoc.id);
        await updateDoc(clientRef, updateData);
        
        clientsMarques++;
        console.log(`✅ Client ${clientDoc.id} marqué comme client`);

      } catch (error) {
        console.error(`❌ Erreur marquage client ${clientDoc.id}:`, error);
        erreurs++;
      }
    }

    console.log(`✅ Marquage terminé: ${clientsMarques} clients marqués, ${erreurs} erreurs`);
    
    return {
      clientsMarques,
      erreurs,
      totalClients: clientsSnapshot.size,
      success: erreurs === 0
    };

  } catch (error) {
    console.error('Erreur marquage clients existants:', error);
    throw error;
  }
};

/**
 * Vérifie l'état des clients dans le système
 */
export const verifierEtatClients = async () => {
  try {
    const clientsCollection = collection(db, 'clients');
    const clientsSnapshot = await getDocs(clientsCollection);
    
    const stats = {
      total: clientsSnapshot.size,
      avecTypeEntite: 0,
      sansTypeEntite: 0,
      clients: 0,
      prospects: 0
    };

    for (const clientDoc of clientsSnapshot.docs) {
      const clientData = clientDoc.data();
      
      if (clientData.typeEntite) {
        stats.avecTypeEntite++;
        if (clientData.typeEntite === ENTITY_TYPES.CLIENT) {
          stats.clients++;
        } else if (clientData.typeEntite === ENTITY_TYPES.PROSPECT) {
          stats.prospects++;
        }
      } else {
        stats.sansTypeEntite++;
      }
    }

    console.log('📊 État des clients:');
    console.log(`- Total: ${stats.total}`);
    console.log(`- Avec typeEntite: ${stats.avecTypeEntite}`);
    console.log(`- Sans typeEntite: ${stats.sansTypeEntite}`);
    console.log(`- Clients: ${stats.clients}`);
    console.log(`- Prospects: ${stats.prospects}`);

    return stats;

  } catch (error) {
    console.error('Erreur vérification état clients:', error);
    throw error;
  }
};

// Exécution directe si appelé en ligne de commande
if (typeof window === 'undefined') {
  // Mode Node.js (pour exécution via script)
  marquerClientsExistants()
    .then(result => {
      console.log('✅ Script terminé avec succès');
      process.exit(0);
    })
    .catch(error => {
      console.error('❌ Erreur script:', error);
      process.exit(1);
    });
}

export default {
  marquerClientsExistants,
  verifierEtatClients
};
